/**
 * InvestigationStats Component - Dashboard Stats Cards
 * Baseado em best practices 2025: KPI cards, metrics design, Tremor-style
 *
 * Padrão:
 * - Cards com ícones coloridos
 * - Valor principal destacado
 * - Descrição/subtítulo
 * - Grid responsivo
 * - Optional trend indicator
 *
 * Referências:
 * - https://www.tremor.so/
 * - https://tailgrids.com/react/components/data-stats
 * - https://www.creative-tim.com/learning-lab/react/statistics-cards/material-dashboard/
 */

import {
  FolderOpenIcon,
  CircleStackIcon,
  ArrowPathIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { formatNumber } from '../../utils/formatters';
import type { Investigation } from '../../types/investigation';

interface InvestigationStatsProps {
  investigations: Investigation[];
}

interface StatCard {
  label: string;
  value: string | number;
  description?: string;
  icon: React.ComponentType<{ className?: string }>;
  iconColor: string;
  iconBgColor: string;
}

/**
 * Cards de estatísticas do dashboard
 *
 * Features:
 * - ✅ 4 cards principais (Investigações, Nós, Arestas, Pendentes)
 * - ✅ Ícones coloridos
 * - ✅ Grid responsivo (1 col → 2 cols → 4 cols)
 * - ✅ Cálculo automático de totais
 * - ✅ Mobile-friendly
 *
 * @example
 * const { investigations } = useInvestigation();
 *
 * <InvestigationStats investigations={investigations} />
 */
export function InvestigationStats({ investigations }: InvestigationStatsProps) {

  // Calcular totais
  const totalInvestigations = investigations.length;

  const totalNos = investigations.reduce(
    (sum, inv) => sum + (inv.estatisticas?.total_nos || 0),
    0
  );

  const totalArestas = investigations.reduce(
    (sum, inv) => sum + (inv.estatisticas?.total_arestas || 0),
    0
  );

  const totalPendentes = investigations.reduce(
    (sum, inv) => sum + (inv.estatisticas?.total_acoes_pendentes || 0),
    0
  );

  const stats: StatCard[] = [
    {
      label: 'Investigações Ativas',
      value: formatNumber(totalInvestigations),
      description: 'Total de investigações criadas',
      icon: FolderOpenIcon,
      iconColor: 'text-blue-600',
      iconBgColor: 'bg-blue-100',
    },
    {
      label: 'Nós no Grafo',
      value: formatNumber(totalNos),
      description: 'Total de nós em todas investigações',
      icon: CircleStackIcon,
      iconColor: 'text-green-600',
      iconBgColor: 'bg-green-100',
    },
    {
      label: 'Arestas (Conexões)',
      value: formatNumber(totalArestas),
      description: 'Total de relacionamentos mapeados',
      icon: ArrowPathIcon,
      iconColor: 'text-purple-600',
      iconBgColor: 'bg-purple-100',
    },
    {
      label: 'Ações Pendentes',
      value: formatNumber(totalPendentes),
      description: 'Nós aguardando expansão',
      icon: ChartBarIcon,
      iconColor: 'text-amber-600',
      iconBgColor: 'bg-amber-100',
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
      {stats.map((stat) => (
        <StatCard key={stat.label} {...stat} />
      ))}
    </div>
  );
}

/**
 * Card individual de estatística
 */
function StatCard({
  label,
  value,
  description,
  icon: Icon,
  iconColor,
  iconBgColor,
}: StatCard) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        {/* Conteúdo */}
        <div className="flex-1">
          <p className="text-xs sm:text-sm text-gray-600 font-medium uppercase tracking-wide">
            {label}
          </p>
          <p className="mt-2 text-2xl sm:text-3xl font-bold text-gray-900">
            {value}
          </p>
          {description && (
            <p className="mt-1 text-xs text-gray-500 line-clamp-1">
              {description}
            </p>
          )}
        </div>

        {/* Ícone */}
        <div className={`flex-shrink-0 ${iconBgColor} rounded-lg p-2 sm:p-3`}>
          <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${iconColor}`} />
        </div>
      </div>
    </div>
  );
}

/**
 * Variante compacta (opcional) - para usar em sidebars
 */
interface CompactStatsProps {
  investigations: Investigation[];
}

export function CompactInvestigationStats({ investigations }: CompactStatsProps) {
  const totalNos = investigations.reduce(
    (sum, inv) => sum + (inv.estatisticas?.total_nos || 0),
    0
  );

  const totalArestas = investigations.reduce(
    (sum, inv) => sum + (inv.estatisticas?.total_arestas || 0),
    0
  );

  return (
    <div className="bg-gray-50 rounded-lg p-3 space-y-2">
      <div className="flex justify-between items-center">
        <span className="text-xs text-gray-600">Investigações:</span>
        <span className="text-sm font-semibold text-gray-900">
          {formatNumber(investigations.length)}
        </span>
      </div>
      <div className="flex justify-between items-center">
        <span className="text-xs text-gray-600">Nós:</span>
        <span className="text-sm font-semibold text-gray-900">
          {formatNumber(totalNos)}
        </span>
      </div>
      <div className="flex justify-between items-center">
        <span className="text-xs text-gray-600">Conexões:</span>
        <span className="text-sm font-semibold text-gray-900">
          {formatNumber(totalArestas)}
        </span>
      </div>
    </div>
  );
}
